import React, { ReactNode, useContext, useEffect } from 'react';
import {
  Container,
  Tab,
  Link as MaterialLink,
  Theme,
  Breadcrumbs,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { CouponPackMasterAboutTab } from './CouponPackMasterAboutTab';
import { CouponPackMasterBulkTab } from './CouponPackMasterBulkTab';
import { CouponPackMasterIssuedListTab } from './CouponPackMasterIssuedListTab';
import { useCouponPackMasterLazyQuery } from '../../../../graphql/generated';

const useStyles = makeStyles((theme: Theme) => ({
  tab: {
    margin: `${theme.spacing(3)}px 0 0`,
  },
  tabPanel: {
    padding: `${theme.spacing(2)}px 0`,
  },
  tabList: {
    backgroundColor: theme.palette.grey[50],
  },
  tabListIndicator: {
    backgroundColor: theme.palette.grey[400],
  },
}));

export type TabConfig = {
  label: string;
  value: string;
  hash: string;
  disabled?: boolean;
  content: ReactNode;
};

export type TabbedPageTemplateProps = {
  couponPackMasterId: string;
};

export const TabbedPageTemplate: React.FC<TabbedPageTemplateProps> = ({ couponPackMasterId }) => {
  const classes = useStyles();
  const { hash } = useLocation();
  const history = useHistory();

  const [couponPackMasterQuery, { data, loading }] = useCouponPackMasterLazyQuery({
    fetchPolicy: 'cache-first',
    onError: (err) => {
      const graphQLErrors = err?.graphQLErrors as any;
      if (graphQLErrors?.[0]?.errorType === '404') {
        history.replace('/not-found');
      }
    },
  });

  useEffect(() => {
    couponPackMasterQuery({
      variables: {
        couponPackMasterId,
      },
    });
  }, [couponPackMasterQuery, couponPackMasterId]);

  if (loading || !data?.couponPackMaster) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const couponPackMaster = data.couponPackMaster;

  const tabs: TabConfig[] = [
    {
      label: '概要',
      value: '#overview',
      hash: '#overview',
      content: (
        <CouponPackMasterAboutTab
          idCouponPackMaster={couponPackMaster?.id}
          nameCouponPackMaster={couponPackMaster?.couponName}
          available={couponPackMaster?.available}
          codeCouponPackMaster={couponPackMaster?.couponCode}
        />
      ),
    },
    {
      label: '一括発行',
      value: '#bulk-issue',
      hash: '#bulk-issue',
      content: <CouponPackMasterBulkTab couponPackMaster={couponPackMaster} />,
    },
    {
      label: '発行済み回数券一覧',
      value: '#issued-list',
      hash: '#issued-list',
      content: <CouponPackMasterIssuedListTab couponPackMasterId={couponPackMaster?.id} />,
    },
  ];

  const activeTab = hash || '#overview' || tabs[0]?.hash || '#tab-1';

  return (
    <Container maxWidth="xl">
      <Breadcrumbs>
        <MaterialLink
          component={Link}
          to={{
            pathname: '/coupon-pack-masters',
            search: history.location.search,
          }}
        >
          回数券マスター覧
        </MaterialLink>
        <Typography variant="body1" color="textPrimary">
          回数券マスタ詳細
        </Typography>
      </Breadcrumbs>

      <TabContext value={activeTab}>
        <TabList className={classes.tab} classes={{ indicator: classes.tabListIndicator, root: classes.tabList }}>
          {tabs.map((tab) => (
            <Tab
              key={tab.hash}
              label={tab.label}
              component={MaterialLink}
              href={tab.hash}
              value={tab.value}
              disabled={tab.disabled}
            />
          ))}
        </TabList>
        {tabs.map((tab) => (
          <TabPanel key={tab.hash} value={tab.value} classes={{ root: classes.tabPanel }}>
            {tab.content}
          </TabPanel>
        ))}
      </TabContext>
    </Container>
  );
};
